/**
 * SharePoint Service - Access SharePoint/OneDrive documents via Microsoft Graph API
 */

import { graphScopes } from "../config/authConfig.js";

let msalInstance = null;
let currentAccount = null;
let sharePointSites = [];
let selectedSite = null;
let selectedFolder = null;

/**
 * Initialize with MSAL instance from authService
 */
export function initSharePointService(msal, account) {
    msalInstance = msal;
    currentAccount = account;
}

/**
 * Get access token for Graph API with SharePoint scopes
 */
async function getGraphToken() {
    if (!msalInstance || !currentAccount) {
        throw new Error("SharePoint service not initialized");
    }

    try {
        // Try silent token acquisition first
        const response = await msalInstance.acquireTokenSilent({
            scopes: graphScopes.sharePoint,
            account: currentAccount
        });
        return response.accessToken;
    } catch (error) {
        // If silent fails, do interactive
        console.log("Silent token acquisition failed, trying interactive...");
        const response = await msalInstance.acquireTokenPopup({
            scopes: graphScopes.sharePoint,
            account: currentAccount
        });
        return response.accessToken;
    }
}

/**
 * Call Microsoft Graph API
 */
async function callGraphAPI(endpoint, method = "GET", body = null) {
    const token = await getGraphToken();
    
    const options = {
        method,
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    };
    
    if (body) {
        options.body = JSON.stringify(body);
    }
    
    const response = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, options);
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || `Graph API error: ${response.status}`);
    }
    
    return response.json();
}

/**
 * Get user's SharePoint sites
 */
export async function getSharePointSites() {
    try {
        console.log("Fetching SharePoint sites...");
        // Get sites the user has access to
        const result = await callGraphAPI("/sites?search=*");
        sharePointSites = result.value || [];
        console.log("SharePoint API returned:", sharePointSites.length, "sites");
        return sharePointSites;
    } catch (error) {
        console.error("Error fetching SharePoint sites:", error);
        // Re-throw to allow UI to show specific error message
        throw error;
    }
}

/**
 * Get user's OneDrive root
 */
export async function getOneDriveRoot() {
    try {
        const result = await callGraphAPI("/me/drive/root/children");
        return result.value || [];
    } catch (error) {
        console.error("Error fetching OneDrive:", error);
        return [];
    }
}

/**
 * Get folders and files in a SharePoint site's document library
 */
export async function getSiteDocuments(siteId, folderId = null) {
    try {
        let endpoint;
        if (folderId) {
            endpoint = `/sites/${siteId}/drive/items/${folderId}/children`;
        } else {
            endpoint = `/sites/${siteId}/drive/root/children`;
        }
        
        const result = await callGraphAPI(endpoint);
        return result.value || [];
    } catch (error) {
        console.error("Error fetching site documents:", error);
        return [];
    }
}

/**
 * Get folders in OneDrive
 */
export async function getOneDriveFolders(folderId = null) {
    try {
        let endpoint;
        if (folderId) {
            endpoint = `/me/drive/items/${folderId}/children`;
        } else {
            endpoint = `/me/drive/root/children`;
        }
        
        const result = await callGraphAPI(endpoint);
        return result.value || [];
    } catch (error) {
        console.error("Error fetching OneDrive folders:", error);
        return [];
    }
}

/**
 * Get file content by ID (OneDrive or SharePoint)
 */
export async function getFileContent(driveId, itemId) {
    try {
        // Get download URL
        const item = await callGraphAPI(`/drives/${driveId}/items/${itemId}`);
        const downloadUrl = item["@microsoft.graph.downloadUrl"];
        
        if (!downloadUrl) {
            throw new Error("No download URL available");
        }
        
        // Download content
        const response = await fetch(downloadUrl);
        const content = await response.text();
        
        return {
            name: item.name,
            content: content,
            size: item.size,
            lastModified: item.lastModifiedDateTime
        };
    } catch (error) {
        console.error("Error fetching file content:", error);
        return null;
    }
}

/**
 * Search for documents across SharePoint/OneDrive
 */
export async function searchDocuments(query, siteId = null) {
    try {
        let endpoint;
        if (siteId) {
            // Search within specific site
            endpoint = `/sites/${siteId}/drive/root/search(q='${encodeURIComponent(query)}')`;
        } else {
            // Search across all accessible content
            endpoint = `/me/drive/root/search(q='${encodeURIComponent(query)}')`;
        }
        
        const result = await callGraphAPI(endpoint);
        return result.value || [];
    } catch (error) {
        console.error("Error searching documents:", error);
        return [];
    }
}

/**
 * Get all text files from a folder for RAG context
 */
export async function getRAGDocuments(driveId, folderId = null, maxFiles = 10) {
    try {
        let endpoint;
        if (folderId) {
            endpoint = `/drives/${driveId}/items/${folderId}/children`;
        } else {
            endpoint = `/drives/${driveId}/root/children`;
        }
        
        const result = await callGraphAPI(endpoint);
        const files = result.value || [];
        
        // Filter for text-based files
        const textExtensions = ['.txt', '.md', '.json', '.csv', '.xml', '.html', '.htm'];
        const textFiles = files.filter(f => 
            !f.folder && textExtensions.some(ext => f.name.toLowerCase().endsWith(ext))
        );
        
        // Get content of files (limited)
        const documents = [];
        for (const file of textFiles.slice(0, maxFiles)) {
            const content = await getFileContent(driveId, file.id);
            if (content) {
                documents.push(content);
            }
        }
        
        return documents;
    } catch (error) {
        console.error("Error fetching RAG documents:", error);
        return [];
    }
}

/**
 * Build RAG context from SharePoint documents
 */
export async function buildSharePointContext(query, config) {
    if (!config || !config.driveId) {
        return null;
    }
    
    try {
        // Option 1: Search for relevant documents
        const searchResults = await searchDocuments(query, config.siteId);
        
        if (searchResults.length === 0) {
            return null;
        }
        
        // Get content from top results
        const maxDocs = 3;
        const maxCharsPerDoc = 2000;
        let context = "";
        
        for (const item of searchResults.slice(0, maxDocs)) {
            if (item.file) {  // Only files, not folders
                try {
                    const content = await getFileContent(item.parentReference.driveId, item.id);
                    if (content && content.content) {
                        const truncated = content.content.substring(0, maxCharsPerDoc);
                        context += `[${content.name}]:\n${truncated}\n\n`;
                    }
                } catch (e) {
                    console.warn(`Could not read file ${item.name}:`, e);
                }
            }
        }
        
        return context.trim() || null;
    } catch (error) {
        console.error("Error building SharePoint context:", error);
        return null;
    }
}

/**
 * Save SharePoint configuration
 */
export function saveSharePointConfig(config) {
    localStorage.setItem("lea_sharepoint_config", JSON.stringify(config));
    selectedSite = config.siteId;
    selectedFolder = config.folderId;
}

/**
 * Load SharePoint configuration
 */
export function loadSharePointConfig() {
    const saved = localStorage.getItem("lea_sharepoint_config");
    if (saved) {
        const config = JSON.parse(saved);
        selectedSite = config.siteId;
        selectedFolder = config.folderId;
        return config;
    }
    return null;
}

/**
 * Clear SharePoint configuration
 */
export function clearSharePointConfig() {
    localStorage.removeItem("lea_sharepoint_config");
    selectedSite = null;
    selectedFolder = null;
}

/**
 * Check if SharePoint is configured
 */
export function isSharePointConfigured() {
    const config = loadSharePointConfig();
    return config && config.driveId;
}

/**
 * Get selected site info
 */
export function getSelectedSite() {
    return selectedSite;
}

/**
 * Get selected folder info
 */
export function getSelectedFolder() {
    return selectedFolder;
}
